import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import {
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  useTheme,
  Divider,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'next-i18next';
import { useStyles } from './useStyles';
import { Layout } from '@web-kc/shared/components';
import ReferenceSidebar from './ReferenceSidebar/ReferenceSidebar';
import { useGetFaqTopics, useGetFaqTopicQuestions } from '@web-kc/core/api';
import { useApp } from '@web-kc/core/providers';

const getLocalizedAnswer = (htmlContent: string, lang: string) => {
  try {
    if (typeof window === 'undefined') return htmlContent;
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const localizedElement = doc.querySelector(`[class$="__${lang}"]`);
    return localizedElement ? localizedElement.innerHTML : htmlContent;
  } catch (error) {
    console.error('Error parsing HTML:', error);
    return '';
  }
};

export const PagesReference = () => {
  const isKc = useApp() === 'kc';
  const theme = useTheme();
  const { classes } = useStyles();
  const { t, i18n } = useTranslation(['reference', 'common']);
  const router = useRouter();
  const [activeTopic, setActiveTopic] = useState<string>();
  const [expandedItem, setExpandedItem] = useState<string | false>(false);

  const { data: topics, isLoading: topicsLoading } = useGetFaqTopics();
  const { data: questions, isLoading: questionsLoading } =
    useGetFaqTopicQuestions({ topicId: activeTopic as string }, !!activeTopic);

  useEffect(() => {
    if (topics?.length && !topics.find((t) => t.id === activeTopic)) {
      setActiveTopic(topics[0].id || '');
    }
  }, [topics, activeTopic]);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      const urlParams = new URLSearchParams(window.location.search);
      const topicId = urlParams.get('topic');

      if (topicId) {
        setActiveTopic(topicId);
      }

      if (hash) {
        setExpandedItem(hash);

        setTimeout(() => {
          const element = document.getElementById(`panel-${hash}`);
          if (element) {
            const rect = element.getBoundingClientRect();
            const viewportHeight = window.innerHeight;

            if (rect.bottom > viewportHeight) {
              const scrollAmount = rect.bottom - viewportHeight + 20;
              window.scrollBy({
                top: scrollAmount,
                behavior: 'smooth',
              });
            }
          }
        }, 300);
      } else {
        setExpandedItem(false);
      }
    };

    handleHashChange();

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleTopicChange = (topic?: string) => {
    setActiveTopic(topic);
    setExpandedItem(false);
  };

  const handleAccordionChange =
    (topicId: string | undefined, itemId: string) =>
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      const newExpanded = isExpanded ? itemId : false;
      setExpandedItem(newExpanded);

      if (isExpanded) {
        router.push(
          {
            pathname: '/reference',
            query: { topic: topicId },
            hash: itemId,
          },
          undefined,
          {
            shallow: true,
            scroll: false,
          },
        );

        setTimeout(() => {
          const element = document.getElementById(`panel-${itemId}`);
          if (element) {
            const rect = element.getBoundingClientRect();
            const viewportHeight = window.innerHeight;

            if (rect.bottom > viewportHeight) {
              const scrollAmount = rect.bottom - viewportHeight + 20;
              window.scrollBy({
                top: scrollAmount,
                behavior: 'smooth',
              });
            }
          }
        }, 300);
      } else {
        router.push(
          {
            pathname: '/reference',
            query: { topic: topicId },
          },
          undefined,
          { shallow: true, scroll: false },
        );
      }
    };

  return (
    <Layout
      paths={['/', '/about-us']}
      labels={[
        `${t('common:breadcrumbs.main')}`,
        `${
          isKc
            ? t('common:breadcrumbs.reference_kc')
            : t('common:breadcrumbs.reference')
        }`,
      ]}
      pageTitle={
        isKc
          ? t('common:pageTitle.reference_kc')
          : t('common:pageTitle.reference')
      }
    >
      <Stack>
        <Typography className={classes.header}>
          {isKc ? t('reference:title_kc') : t('reference:title')}
        </Typography>

        {topicsLoading ? (
          <Box mt={2}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={4}
            alignItems="flex-start"
            mt={2}
          >
            <ReferenceSidebar
              faqData={(topics || []).map((t) => ({
                id: t.id || '',
                name: t.name || '',
                items: [],
              }))}
              activeTopic={activeTopic}
              setActiveTopic={handleTopicChange}
            />

            {questionsLoading ? (
              <Box mt={2}>
                <CircularProgress />
              </Box>
            ) : (
              <Box
                height="90%"
                width="100%"
                sx={{
                  borderRadius: 1,
                  boxShadow: `${theme.shadows[2]}`,
                  bgcolor: '#ffffff',
                }}
              >
                <Box id={`topic-${activeTopic}`}>
                  {questions?.map((item) => (
                    <div key={item.id}>
                      <Accordion
                        id={`panel-${item.id}`}
                        expanded={expandedItem === item.id}
                        onChange={handleAccordionChange(
                          activeTopic,
                          item.id || '',
                        )}
                        className={classes.accordionItem}
                        disableGutters
                        elevation={0}
                      >
                        <AccordionSummary
                          id={`${item.id}-summary`}
                          expandIcon={
                            <ExpandMoreIcon className={classes.expandIcon} />
                          }
                          className={classes.accordionSummary}
                        >
                          <Typography
                            className={classes.accordionQuestion}
                            sx={
                              expandedItem === item.id
                                ? { color: theme.palette.warning.main }
                                : { color: theme.palette.primary.main }
                            }
                          >
                            {item.question}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails className={classes.accordionDetails}>
                          <Typography
                            component="div"
                            dangerouslySetInnerHTML={{
                              __html: getLocalizedAnswer(
                                item.answer || '',
                                i18n.language,
                              ),
                            }}
                          />
                        </AccordionDetails>
                      </Accordion>

                      <Divider sx={{ mx: 3 }} />
                    </div>
                  ))}
                </Box>
              </Box>
            )}
          </Stack>
        )}
      </Stack>
    </Layout>
  );
};
